import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent, CartCountOptimisticEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 * @property {HTMLElement} cartBubbleAccessibleCount - The visually-hidden, screen-reader-only count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount', 'cartBubbleAccessibleCount'];

  /** Items reflected optimistically (before the server has confirmed them), so the real cart:update that follows doesn't double-count them. @type {number} */
  #pendingOptimisticCount = 0;

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';

    // Keep the screen-reader-only count in sync too - it's rendered once server-side
    const label = this.refs.cartBubbleAccessibleCount.dataset.label ?? '';
    this.refs.cartBubbleAccessibleCount.textContent = `${label}: ${value}`;
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.addEventListener(ThemeEvents.cartCountOptimistic, this.onOptimisticCount);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.removeEventListener(ThemeEvents.cartCountOptimistic, this.onOptimisticCount);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    if (comingFromProductForm && this.#pendingOptimisticCount > 0) {
      // Already reflected via the optimistic bump dispatched on click (see
      // onOptimisticCount) - consume the pending marker instead of adding
      // itemCount a second time. Any leftover (e.g. a queued batch add that
      // never went through the optimistic path) still gets added normally.
      const consumed = Math.min(itemCount, this.#pendingOptimisticCount);
      this.#pendingOptimisticCount -= consumed;

      const remaining = itemCount - consumed;
      if (remaining > 0) {
        this.renderCartBubble(remaining, true);
      }
      return;
    }

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Optimistically bumps (or rolls back) the visible cart count before the
   * server has confirmed the add-to-cart request, so the badge updates in
   * the same frame as the button's "Added" confirmation instead of waiting
   * on the network round trip.
   * @param {CartCountOptimisticEvent} event - The optimistic count event.
   */
  onOptimisticCount = (event) => {
    const delta = event.detail?.delta ?? 0;
    if (delta === 0) return;

    this.#pendingOptimisticCount = Math.max(0, this.#pendingOptimisticCount + delta);

    const newCount = Math.max(0, this.currentCartCount + delta);
    this.renderCartBubble(newCount, false, delta > 0);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        // Ask the cart instead of trusting the stored value.
        fetch('/cart.js')
          .then((response) => response.json())
          .then((cart) => {
            if (cart.item_count >= 0) {
              this.renderCartBubble(cart.item_count, false, false);
            }
          })
          .catch(() => {});
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
